import { useEffect, useMemo, useState } from "react";
import { AccessoireItem } from "../Solutions/SolutionDetails/SolutionAccessoires";
import { InstallationType } from "./useGetSolutionsQuery";

export const useGetAccessoiresInfo = (
  solution?: Solution | null,
  installationType?: InstallationType,
  accessoires?: {
    selectedAccessoiresNonSupp?: Record<string, boolean>;
    selectedServices?: Record<string, boolean>;
    selectedBoilers?: Record<string, boolean>;
    selectedAccessoiresSupp?: Record<string, number>;
  },
) => {
  const [selectedAccessoiresNonSupp, setSelectedAccessoiresNonSupp] = useState<
    Record<string, boolean>
  >(accessoires?.selectedAccessoiresNonSupp ?? {});

  const toggleSelectedAccessoireNonSupp = (code: string) =>
    setSelectedAccessoiresNonSupp((prev) => ({ ...prev, [code]: !prev[code] }));

  const [selectedServices, setSelectedServices] = useState<
    Record<string, boolean>
  >(
    accessoires?.selectedServices ??
    Object.fromEntries(solution?.services?.filter((s) => s.dans_panier === true).map((s) => [s.code, true]) ?? []),
  );

  const toggleSelectedService = (code: string) =>
    setSelectedServices((prev) => ({ ...prev, [code]: !prev[code] }));

  const [selectedBoilers, setSelectedBoliers] = useState<
    Record<string, boolean>
  >(
    accessoires?.selectedBoilers ??
    (installationType === InstallationType.hybride && solution?.chaudiere?.code
      ? { [solution.chaudiere.code]: true }
      : {})
  );

  const toggleSelectedBoilers = (code: string) =>
    setSelectedBoliers((prev) => ({ ...prev, [code]: !prev[code] }));

  const [selectedAccessoiresSupp, setSelectedAccessoiresSupp] = useState<
    Record<string, number | undefined>
  >(accessoires?.selectedAccessoiresSupp ?? {});

  const setSelectedAccessoireSuppCount = (code: string, count?: number) =>
    setSelectedAccessoiresSupp((prev) => ({ ...prev, [code]: count }));

  const addAccessoireSuppCount = (code: string) =>
    setSelectedAccessoiresSupp((prev) => ({
      ...prev,
      [code]: (prev[code] ?? 0) + 1,
    }));

  const decreaseAccessoireSuppCount = (code: string) =>
    setSelectedAccessoiresSupp((prev) => ({
      ...prev,
      [code]: (prev[code] ?? 0) - 1,
    }));

  useEffect(() => {
    if (!solution) return;

    const defaultServices = Object.fromEntries(
      solution.services
        .filter((s: any) => s.thercodim_active && s.dans_panier === true)
        .map((s) => [s.code, true]),
    );
    const defaultBoilers =
      installationType === InstallationType.hybride &&
        solution.chaudiere?.code &&
        solution.chaudiere?.thercodim_active
        ? { [solution.chaudiere.code]: true }
        : {};

    if (accessoires) {
      setSelectedAccessoiresNonSupp(
        Object.fromEntries(
          Object.entries(accessoires.selectedAccessoiresNonSupp ?? {}).filter(
            ([code, value]) =>
              !!value &&
              solution.accessoires.some(
                (a) =>
                  a.code === code && a.thercodim_active && a.tag !== "SUPP",
              ),
          ),
        ),
      );
      setSelectedServices(
        Object.keys(accessoires.selectedServices ?? {}).length > 0
          ? Object.fromEntries(
            Object.entries(accessoires.selectedServices ?? {}).filter(
              ([code, value]) =>
                !!value &&
                solution.services.some(
                  (s) => s.code === code && s.thercodim_active && s.dans_panier === true,
                ),
            ),
          )
          : defaultServices,
      );
      setSelectedAccessoiresSupp(
        Object.fromEntries(
          Object.entries(accessoires.selectedAccessoiresSupp ?? {}).filter(
            ([code, value]) =>
              !!value &&
              solution.accessoires.some(
                (a) =>
                  a.code === code && a.thercodim_active && a.tag === "SUPP",
              ),
          ),
        ),
      );
      setSelectedBoliers(
        Object.keys(accessoires.selectedBoilers ?? {}).length > 0
          ? Object.fromEntries(
            Object.entries(accessoires.selectedBoilers ?? {}).filter(
              ([code, value]) =>
                !!value &&
                solution.chaudiere?.code === code &&
                solution.chaudiere?.thercodim_active,
            ),
          )
          : defaultBoilers,
      );
    } else {
      // No prior save — apply defaults
      setSelectedServices(defaultServices);
      setSelectedBoliers(defaultBoilers);
    }
  }, [accessoires, solution]);

  const items = useMemo(() => {
    if (!solution) {
      return;
    }
    const { panier } = solution;
    const mapper = (item: Accessoire & { isService?: boolean }) => {
      const id = (item as any)?.id as number | string | undefined;
      const {
        prix,
        nom: name,
        previewUrl,
        code,
        description,
        tag,
        quantite,
        salesreference,
        acc_incompatibility,
        // isService,
      } = item;

      return {
        code,
        id,
        salesreference,
        name,
        // :         isService ? (
        //   <group data-align="center" data-gap="5" data-wrap="no">
        //     {name}
        //     <Badge outline={false} filled color="green">
        //       {t("FO_KEY_service")}
        //     </Badge>
        //   </group>
        // ) :
        //  (
        //   name
        // ),
        description,
        prix,
        previewUrl,
        quantite,
        tag,
        acc_incompatibility,
      };
    };

    const panierItems = panier.filter((i: any) => i.thercodim_active).map(mapper);

    const serviceItems = solution.services
      .filter((i: any) => i.thercodim_active)
      .map(mapper);

    const accessoireItems = solution.accessoires
      .filter((i: any) => i.thercodim_active)
      .map(mapper);

    let boilerItems: AccessoireItem[] = [];

    if (
      installationType == InstallationType.hybride &&
      solution.chaudiere?.thercodim_active
    ) {
      const { nom, prix, code, qte, description, previewUrl, tag } =
        solution.chaudiere ?? {};
      boilerItems = [
        {
          name: nom,
          code,
          prix,
          description,
          quantite: qte,
          previewUrl,
          tag: tag ?? "",
        },
      ];
    }
    return {
      panierItems,
      serviceItems,
      accessoireItems,
      boilerItems,
    };
  }, [solution, installationType]);

  return {
    items,
    selectionInfo: {
      selectedAccessoiresNonSupp,
      toggleSelectedAccessoireNonSupp,
      selectedServices,
      toggleSelectedService,
      selectedAccessoiresSupp,
      setSelectedAccessoireSuppCount,
      addAccessoireSuppCount,
      decreaseAccessoireSuppCount,
      selectedBoilers,
      toggleSelectedBoilers,
    },
  };
};
